import React, { useEffect, useRef } from "react";
import Flatpickr from "react-flatpickr";

type Mode = "date" | "time" | "datetime";

interface Props {
  value?: string;
  onChange?: (value: string) => void;
  mode?: Mode;
  placeholder?: string;
  minDate?: Date | string;
  className?: string;
}

const formatDate = (d: Date) => d.toISOString().split("T")[0];
const formatTime = (d: Date) => {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const DateTimePicker: React.FC<Props> = ({
  value = "",
  onChange,
  mode = "date",
  placeholder,
  minDate,
  className = "",
}) => {
  const instRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      // cleanup any attached handlers
      const inst = instRef.current;
      if (inst && (inst as any)._fpOutsideClickHandler) {
        document.removeEventListener(
          "mousedown",
          (inst as any)._fpOutsideClickHandler
        );
        delete (inst as any)._fpOutsideClickHandler;
      }
    };
  }, []);

  const attachOutsideClose = (instance: any) => {
    if (typeof document === "undefined" || !instance) return;
    const handler = (e: MouseEvent) => {
      const cal = instance.calendarContainer as HTMLElement | null;
      const input = instance.input as HTMLElement | null;
      const target = e.target as Node;
      if (!cal) return;
      if (cal.contains(target) || (input && input.contains(target))) return;
      instance.close();
    };
    document.addEventListener("mousedown", handler);
    (instance as any)._fpOutsideClickHandler = handler;
  };

  const detachOutsideClose = (instance: any) => {
    if (typeof document === "undefined" || !instance) return;
    const handler = (instance as any)._fpOutsideClickHandler;
    if (handler) document.removeEventListener("mousedown", handler);
    delete (instance as any)._fpOutsideClickHandler;
  };

  const commonOptions: any = {
    appendTo: typeof document !== "undefined" ? document.body : undefined,
    onOpen: (_d: any, _s: any, inst: any) => {
      instRef.current = inst;
      attachOutsideClose(inst);
    },
    onClose: (_d: any, _s: any, inst: any) => {
      detachOutsideClose(inst);
    },
  };

  if (mode === "date") {
    commonOptions.dateFormat = "Y-m-d";
    commonOptions.minDate = minDate || undefined;
  }
  if (mode === "time") {
    commonOptions.enableTime = true;
    commonOptions.noCalendar = true;
    commonOptions.dateFormat = "H:i";
  }
  if (mode === "datetime") {
    commonOptions.enableTime = true;
    commonOptions.dateFormat = "Y-m-d H:i";
  }

  return (
    <Flatpickr
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={(dates: any) => {
        const d = dates[0] as Date | undefined;
        if (!d) {
          onChange?.("");
          return;
        }
        let out: string | undefined;
        if (mode === "date") out = formatDate(d);
        else if (mode === "time") out = formatTime(d);
        else out = d.toISOString();
        if (out) onChange?.(out);
      }}
      options={commonOptions}
    />
  );
};

export default DateTimePicker;
